import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { InputLabel } from '../ui/InputLabel';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type FormValues = {
  email: string;
};

export default function ForgotPasswordForm({
  onSubmit,
  loading,
  success,
}: Readonly<{
  onSubmit?: (data: FormValues) => Promise<void> | void;
  loading?: boolean;
  formError?: string | null;
  success?: boolean;
}>) {
  const { t } = useTranslation(['auth', 'common']);
  const schema = useMemo(
    () =>
      z.object({
        email: z.email(t('errors.email')),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
    mode: 'onChange',
  });

  const isValid = form.formState.isValid;
  const submittedEmail = form.getValues('email');

  if (success) {
    return (
      <div className="space-y-16">
        <p className="text-sm text-secondary-inverse">
          {t('auth:forgotPasswordSuccessMessage', { submittedEmail })}
        </p>

        <Button className="w-full text-base h-11" asChild>
          <Link to="/login">{t('auth:backToLogin')}</Link>
        </Button>

        <p className="text-sm text-secondary-inverse">
          {t('auth:checkSpamMessage')}{' '}
          <span className="text-button-secondary">{t('auth:supportEmail')}</span>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(async (data) => onSubmit && (await onSubmit(data)))}>
      <div className="space-y-6">
        <div className="space-y-1">
          <Controller
            name="email"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('auth:email')}
                id="email"
                type="email"
                className="border-input-inverse bg-transparent"
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {form.formState.errors.email && (
            <p className="text-xs text-error">{form.formState.errors.email.message}</p>
          )}
        </div>

        <p className="text-xs text-secondary-inverse">{t('auth:addressInDbMessage')}</p>

        <Button
          type="submit"
          className="w-full h-11 rounded-full text-base disabled:bg-button-primary-disable-inverse disabled:text-disable"
          disabled={!isValid || loading}
        >
          {loading ? t('common:loading') : t('auth:sendResetLink')}
        </Button>
      </div>
    </form>
  );
}
